import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface CreditsInfo {
    plan_type: 'free' | 'pro' | 'business';
    messages_today: number;
    messages_month: number;
    daily_limit: number;
    monthly_limit: number;
    max_output_tokens: number;
}

export function useCredits() {
    const { user } = useAuth();
    const [credits, setCredits] = useState<CreditsInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchCredits = useCallback(async () => {
        if (!user?.id) {
            setCredits(null);
            setIsLoading(false);
            return;
        }

        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data, error: rpcError } = await (supabase.rpc as any)('get_usage_stats', {
                p_user_id: user.id
            });

            if (rpcError) {
                console.error('[useCredits] Error:', rpcError);
                setError(rpcError.message);
                setCredits(null);
            } else if (data && Array.isArray(data) && data.length > 0) {
                setCredits(data[0] as CreditsInfo);
                setError(null);
            }
        } catch (err) {
            console.error('[useCredits] Exception:', err);
            setError('Failed to fetch credits');
        } finally {
            setIsLoading(false);
        }
    }, [user?.id]);

    useEffect(() => {
        fetchCredits();
    }, [fetchCredits]);

    // Calculate remaining credits
    const remainingToday = credits
        ? Math.max(0, credits.daily_limit - credits.messages_today)
        : 0;

    const remainingMonth = credits
        ? Math.max(0, credits.monthly_limit - credits.messages_month)
        : 0;

    // For free users, show daily limit; for paid, show monthly
    const displayRemaining = credits?.plan_type === 'free'
        ? remainingToday
        : remainingMonth;

    const displayLimit = credits?.plan_type === 'free'
        ? credits.daily_limit
        : credits?.monthly_limit || 0;

    return {
        credits,
        isLoading,
        error,
        refetch: fetchCredits,
        remainingToday,
        remainingMonth,
        displayRemaining,
        displayLimit,
        isLimitReached: displayRemaining <= 0,
        planLabel: credits?.plan_type === 'business' ? 'Business'
            : credits?.plan_type === 'pro' ? 'Pro'
                : 'Free',
    };
}
